/********************************************************************
 * @author:      Kaven
 * @email:       kaven@wuwenkai.com
 * @website:     http://blog.kaven.xyz
 * @file:        [Kaven-Basic] /src/libs/class/KavenUrl.ts
 * @create:      2022-04-14 15:56:19.960
 * @modify:      2025-07-10 15:22:48.214
 * @version:     6.0.0
 * @times:       40
 * @lines:       152
 * @copyright:   Copyright © 2022-2025 Kaven. All Rights Reserved.
 * @description: [description]
 * @license:     [license]
 ********************************************************************/

import { TrimEnd } from "../string/TrimEnd";

/**
 * Represents a utility class for handling and parsing URLs.
 * @since 4.0.0
 * @version 2023-11-30
 * @see https://developer.mozilla.org/en-US/docs/Learn/Common_questions/What_is_a_URL
 */
export class KavenUrl {
    public readonly OriginalUrl: string;

    public constructor(url = "") {
        this.OriginalUrl = url;
        this.TryParse(url);
    }

    public Scheme?: string;
    public DomainName?: string;
    public Port?: number;
    public Path?: string;
    public Parameters?: string;
    public Anchor?: string;

    public get PathAndParameters(): string {
        let str = "";

        if (this.Path !== undefined) {
            str += this.Path;
        }

        if (this.Parameters !== undefined) {
            str += this.Parameters;
        }

        return str;
    }

    public ToString(): string {
        let str = "";

        if (this.Scheme !== undefined) {
            str = `${this.Scheme}://`;
        }

        if (this.DomainName !== undefined) {
            str += this.DomainName;
        }

        if (this.Port !== undefined) {
            str += `:${this.Port}`;
        }

        if (this.Path !== undefined) {
            str += this.Path;
        }

        if (this.Parameters !== undefined) {
            str += this.Parameters;
        }

        if (this.Anchor !== undefined) {
            str += this.Anchor;
        }

        return str;
    }

    public TryParse(url: string): boolean {
        try {
            // Check if the URL contains a protocol (e.g., "http://")
            if (url.includes("://")) {
                const u = new URL(url);

                // Extract scheme, domain name, and port from the URL
                this.Scheme = TrimEnd(u.protocol, ":");
                this.DomainName = u.hostname;

                // Determine port based on the scheme if not specified
                if (u.port.length > 0) {
                    this.Port = Number(u.port);
                } else {
                    const protocolPort = [
                        ["ftp", 21],
                        ["http", 80],
                        ["https", 443],
                        ["ws", 80],
                        ["wss", 443],
                    ];

                    for (const [protocol, port] of protocolPort) {
                        if (this.Scheme.toLowerCase() === protocol) {
                            this.Port = port as number;
                            break;
                        }
                    }
                }

                // Extract path, parameters, and anchor from the URL
                this.Path = u.pathname;
                this.Parameters = u.search;
                this.Anchor = u.hash;
            } else if (url.includes(":") || !url.startsWith("/")) {
                // If no protocol is provided, assume "http://"
                const u = new URL("http://" + url);
                this.DomainName = u.hostname;

                // Extract port
                if (u.port.length > 0) {
                    this.Port = Number(u.port);
                } else {
                    const parts = url.split(":");
                    if (parts.length === 2) {
                        this.Port = parseInt(parts[1], 10);
                    }
                }

                // Extract path, parameters, and anchor from the URL
                this.Path = u.pathname;
                this.Parameters = u.search;
                this.Anchor = u.hash;
            } else {
                // If no protocol is provided, assume "http://"
                const u = new URL("http://kaven.xyz" + url);

                // Extract path, parameters, and anchor from the URL
                this.Path = u.pathname;
                this.Parameters = u.search;
                this.Anchor = u.hash;
            }
        } catch {
            return false;
        }

        return true;
    }
}
